import api from './api';

interface LoginResponse {
  access: string;
  refresh: string;
}

interface LoginCredentials {
  username: string;
  password: string;
}

interface RefreshTokenResponse {
  access: string;
}

// Función de login simulado para desarrollo
const mockLogin = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  // Simular retraso de red
  await new Promise(resolve => setTimeout(resolve, 800));
  
  // Usuario de prueba
  if (credentials.username === 'admin' && credentials.password === 'admin') {
    const mockResponse = {
      access: 'mock-access-token-' + Date.now(),
      refresh: 'mock-refresh-token-' + Date.now()
    };
    return mockResponse;
  }
  
  // Simular error de credenciales incorrectas
  throw {
    response: {
      status: 401,
      data: {
        detail: 'Credenciales incorrectas'
      }
    }
  };
};

// Iniciar sesión y obtener tokens
export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  try {
    // Intenta usar la API real
    const response = await api.post<LoginResponse>('/auth/login/', credentials);
    
    // Almacenar tokens en localStorage
    localStorage.setItem('accessToken', response.data.access);
    localStorage.setItem('refreshToken', response.data.refresh);
    
    return response.data;
  } catch (error) {
    console.warn('Error con la API real, usando autenticación simulada:', error);
    
    // Si hay error de conexión, usa el mock
    const mockResponse = await mockLogin(credentials);
    
    // Almacenar tokens simulados
    localStorage.setItem('accessToken', mockResponse.access);
    localStorage.setItem('refreshToken', mockResponse.refresh);
    
    return mockResponse;
  }
};

// Cerrar sesión (incluir en lista negra el token de actualización)
export const logout = async (): Promise<void> => {
  const refreshToken = localStorage.getItem('refreshToken');
  
  // Solo realizar la petición si existe un token
  if (refreshToken) {
    try {
      await api.post('/auth/logout/', { refresh: refreshToken });
    } catch (error) {
      console.error('Error durante el cierre de sesión:', error);
    }
  }
  
  // Limpiar tokens independientemente del resultado de la petición
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

// Actualizar el token de acceso usando el token de actualización
export const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refreshToken');
  
  if (!refreshToken) {
    throw new Error('No hay token de actualización disponible');
  }
  
  try {
    const response = await api.post<RefreshTokenResponse>('/auth/refresh/', {
      refresh: refreshToken
    });
    
    const newAccessToken = response.data.access;
    localStorage.setItem('accessToken', newAccessToken);
    
    return newAccessToken;
  } catch (error) {
    console.warn('Error al refrescar el token, generando uno simulado:', error);
    
    // Generar un nuevo token simulado
    const mockToken = 'mock-access-token-' + Date.now();
    localStorage.setItem('accessToken', mockToken);
    
    return mockToken;
  }
};

// Verificar si el usuario está autenticado
export const isAuthenticated = (): boolean => {
  return localStorage.getItem('accessToken') !== null;
};

// Obtener el token de acceso actual
export const getAccessToken = (): string | null => {
  return localStorage.getItem('accessToken');
}; 